import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import { useNavigate } from "fusion-plugin-react-router";
import ConnectQuoteDialog from "../features/quote/components/ConnectQuoteDialog";
import Navbar from "../shared/components/navbar";
import Footer from "../shared/components/footer";
import {
  UBER_AUTH_CLIENT_ID,
  UBER_AUTH_RESPONSE_TYPE,
  UBER_AUTH_REDIRECT_PORTAL_LOGIN_PATH,
} from "../config/config";
import { TrackHotjarEvent } from "../shared/utils/trackHotjarEvent";
import { HotjarEventNames } from "../shared/utils/hotjarEventNames";
import Button from "../shared/components/Button";

const insurers = [
  {
    name: "AXA",
    src: assetUrl("../assets/landing/insurers/axa.png"),
    maxHeight: 30,
  },
  {
    name: "Sabre",
    src: assetUrl("../assets/landing/insurers/sabre.png"),
    maxHeight: 34,
  },
  {
    name: "Freeway",
    src: assetUrl("../assets/landing/insurers/freeway.png"),
    maxHeight: 24,
  },
  {
    name: "INSHUR",
    src: assetUrl("../assets/landing/insurers/inshur.svg"),
    maxHeight: 19,
  },
  {
    name: "KGM",
    src: assetUrl("../assets/landing/insurers/kgm.png"),
    maxHeight: 26,
  },
  {
    name: "ERS",
    src: assetUrl("../assets/landing/insurers/ers.png"),
    maxHeight: 40,
  },
  {
    name: "QBE",
    src: assetUrl("../assets/landing/insurers/qbe.png"),
    maxHeight: 34,
  },
];

const TaxiLandingPage = () => {
  const navigate = useNavigate();
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const quoteButtonRef = useRef(null);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_LANDING_PAGE_VIEWED);
  }, []);

  return (
    <div
      className="tw-m-0 tw-box-border tw-bg-white tw-text-[17px] tw-leading-[1.5] tw-text-dis-navy [&_*]:tw-box-border"
      lang="en-GB"
    >
      <Helmet>
        <title>
          Compare private hire insurance quotes | Driver Insurance Services
        </title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="description"
          content="Compare private hire insurance quotes from a selected panel of Uber and Instadoc approved insurers. Driver Insurance Services, powered by Freeway."
        />
      </Helmet>

      <a
        className="tw-absolute tw-left-3 tw-top-[-60px] tw-z-[100] tw-rounded-lg tw-bg-white tw-px-3.5 tw-py-2.5 tw-font-bold tw-text-dis-navy focus:tw-top-3 focus-visible:tw-outline-[3px] focus-visible:tw-outline-dis-navy focus-visible:tw-outline-offset-2 focus-visible:tw-shadow-dis-focus"
        href="#taxi-landing-main"
      >
        Skip to main content
      </a>

      <Navbar />

      <main id="taxi-landing-main">
        <section
          className="tw-relative tw-overflow-hidden tw-bg-dis-hero tw-pb-8 tw-pt-7 tw-text-white lg:tw-pb-20 lg:tw-pt-[72px]"
          aria-labelledby="taxi-landing-title"
        >
          <span
            className="tw-hidden lg:tw-absolute lg:tw-right-[-120px] lg:tw-top-[-120px] lg:tw-block lg:tw-h-[420px] lg:tw-w-[420px] lg:tw-rounded-full lg:tw-bg-white/[0.16]"
            aria-hidden="true"
          />
          <div className="tw-relative tw-mx-auto tw-flex tw-max-w-[1120px] tw-flex-wrap tw-items-center tw-gap-x-[72px] tw-gap-y-8 tw-px-4">
            <div className="tw-flex tw-min-w-0 tw-flex-[1_1_460px] tw-flex-col tw-gap-4 lg:tw-gap-[22px]">
              <p className="tw-m-0 tw-inline-flex tw-self-start tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-py-1.5 tw-pr-3 tw-pl-2 tw-text-sm tw-font-bold tw-text-dis-deep-blue">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M12 3l7 3v5.5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5V6l7-3Z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                Uber and Instadoc approved
              </p>
              <h1
                id="taxi-landing-title"
                className="tw-m-0 tw-max-w-[13ch] tw-text-[38px] tw-font-extrabold tw-leading-[1.04] tw-tracking-[-0.035em] tw-text-white lg:tw-text-[clamp(46px,5vw,64px)]"
              >
                Compare private hire insurance quotes
              </h1>
              <div className="tw-flex tw-max-w-[420px] tw-flex-col tw-gap-2.5">
                <Button
                  ref={quoteButtonRef}
                  variant="home"
                  size="hero"
                  fullWidth
                  type="button"
                  aria-haspopup="dialog"
                  aria-controls="connect-dialog"
                  data-track="hero-get-quote"
                  onClick={() => {
                    TrackHotjarEvent(
                      HotjarEventNames.TAXI_LANDING_GET_QUOTE_CLICKED,
                    );
                    setQuoteDialogOpen(true);
                  }}
                >
                  Get a new quote
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M5 12h14" />
                    <path d="M13 6l6 6-6 6" />
                  </svg>
                </Button>
                <p className="tw-m-0 tw-text-center tw-text-[15px] tw-text-white">
                  Been here before?{" "}
                  <a
                    href="/landing"
                    data-track="hero-log-in"
                    className="tw-font-bold tw-text-white tw-underline-offset-[3px] hover:tw-bg-transparent focus-visible:tw-outline-[3px] focus-visible:tw-outline-white focus-visible:tw-outline-offset-2"
                    onClick={() =>
                      TrackHotjarEvent(
                        HotjarEventNames.TAXI_LANDING_LOG_IN_CLICKED,
                      )
                    }
                  >
                    Log in
                  </a>
                </p>
              </div>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-5 tw-gap-y-2 tw-p-0">
                <li className="tw-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-semibold">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  Selected panel of insurers
                </li>
                <li className="tw-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-semibold">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  FCA regulated
                </li>
                <li className="tw-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-semibold">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  UK support team
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section
          className="tw-bg-white tw-pb-6 tw-pt-5 md:tw-pb-9 md:tw-pt-7"
          aria-labelledby="taxi-landing-panel-title"
        >
          <div className="tw-mx-auto tw-max-w-[1120px] tw-px-4">
            <div className="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-center tw-gap-3">
              <h2
                id="taxi-landing-panel-title"
                className="tw-col-start-2 tw-m-0 tw-text-center tw-text-[15px] tw-font-bold tw-text-dis-text-2"
              >
                Panel of insurers
              </h2>
              <button
                type="button"
                className="tw-appearance-none tw-hidden md:tw-col-start-3 md:tw-inline-flex md:tw-min-h-9 md:tw-items-center md:tw-justify-self-end md:tw-rounded-full md:tw-border-[1.5px] md:tw-border-solid md:tw-border-dis-border-strong md:tw-bg-white md:tw-px-3 md:tw-text-sm md:tw-font-bold md:tw-leading-[1.5] md:tw-text-dis-navy md:tw-cursor-pointer focus-visible:tw-outline-[3px] focus-visible:tw-outline-dis-navy focus-visible:tw-outline-offset-2 focus-visible:tw-shadow-dis-focus"
                aria-pressed={manuallyPaused}
                onClick={() => setManuallyPaused((current) => !current)}
              >
                {manuallyPaused ? "Play" : "Pause"}
                <span className="tw-sr-only"> the insurer logos</span>
              </button>
            </div>
            <div
              className="group tw-landing-logo-mask tw-mt-2.5 tw-overflow-hidden motion-reduce:tw-overflow-x-auto"
              tabIndex={0}
              aria-label="Panel of insurer logos"
              onTouchStart={() => setInteractionPaused(true)}
              onTouchEnd={() => setInteractionPaused(false)}
              onMouseEnter={() => setInteractionPaused(true)}
              onMouseLeave={() => setInteractionPaused(false)}
              onFocus={() => setInteractionPaused(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget))
                  setInteractionPaused(false);
              }}
            >
              <div
                className="tw-flex tw-w-max tw-animate-taxi-marquee motion-reduce:tw-animate-none"
                style={{
                  animationPlayState:
                    manuallyPaused || interactionPaused ? "paused" : "running",
                }}
              >
                <ul className="tw-m-0 tw-flex tw-shrink-0 tw-list-none tw-gap-3 tw-p-0 tw-pr-3">
                  {insurers.map((insurer) => (
                    <li
                      key={insurer.name}
                      className="tw-flex tw-h-[60px] tw-w-[112px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-border tw-border-solid tw-border-dis-border tw-bg-white tw-px-2.5 md:tw-h-[76px] md:tw-w-[150px]"
                    >
                      <img
                        className="tw-block tw-w-auto tw-max-w-full tw-object-contain"
                        src={insurer.src}
                        alt={insurer.name}
                        style={{ maxHeight: insurer.maxHeight }}
                        loading="lazy"
                      />
                    </li>
                  ))}
                </ul>
                <ul
                  className="tw-m-0 tw-flex tw-shrink-0 tw-list-none tw-gap-3 tw-p-0 tw-pr-3"
                  aria-hidden="true"
                >
                  {insurers.map((insurer) => (
                    <li
                      key={insurer.name}
                      className="tw-flex tw-h-[60px] tw-w-[112px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-border tw-border-solid tw-border-dis-border tw-bg-white tw-px-2.5 md:tw-h-[76px] md:tw-w-[150px]"
                    >
                      <img
                        className="tw-block tw-w-auto tw-max-w-full tw-object-contain"
                        src={insurer.src}
                        alt=""
                        style={{ maxHeight: insurer.maxHeight }}
                        loading="lazy"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="tw-mb-0 tw-mt-2.5 tw-text-center tw-text-sm tw-text-dis-text-muted">
              A selected panel of insurers, not the whole market.
            </p>
          </div>
        </section>
      </main>

      <Footer />

      <ConnectQuoteDialog
        isOpen={quoteDialogOpen}
        openerRef={quoteButtonRef}
        navigate={navigate}
        onClose={() => setQuoteDialogOpen(false)}
        onContinue={() => {
          window.location.href = `https://login.uber.com/oauth/v2/authorize?client_id=${UBER_AUTH_CLIENT_ID}&response_type=${UBER_AUTH_RESPONSE_TYPE}&redirect_uri=${window
            .location.origin + UBER_AUTH_REDIRECT_PORTAL_LOGIN_PATH}`;
        }}
      />
    </div>
  );
};

export default TaxiLandingPage;
